import { Settings } from "./settings";

// Helper: converts multiplier-only config into pixel values
const buildPaddingConfig = (configs) =>
  Object.fromEntries(
    Object.entries(configs).map(([key, { sides, top, bottom }]) => [
      key,
      {
        paddingSides: Settings.SYMBOL_WIDTH * sides,
        paddingTop: Settings.SYMBOL_HEIGHT * top,
        paddingBottom: Settings.SYMBOL_HEIGHT * bottom,
      },
    ]),
  );

export const PixiSettings = {
  // Multipliers only — auto-multiplied by SYMBOL_WIDTH (sides) and SYMBOL_HEIGHT (top/bottom)
  REELSET_LAYOUT_CONFIGS: buildPaddingConfig({
    // MOBILE_SMALL_PORTRAIT: { sides: 0.35, top: 2.5, bottom: 2.5 },
    // MOBILE_SMALL_PORTRAIT: { sides: 0.45, top: 2.5, bottom: 2.5 }, // the reel a touch smaller (2026-10-30)
    // MOBILE_SMALL_PORTRAIT: { sides: 0.5, top: 2.5, bottom: 2.5 }, // a bit more
    MOBILE_SMALL_PORTRAIT: { sides: 0.485, top: 2.5, bottom: 2.5 },
    // MOBILE_SMALL_LANDSCAPE: { sides: 5.0, top: 0.7, bottom: 0.7 },
    // Bigger (2026-10-16): width-bound here, so less side room; ~13 px over
    // the gold bar and over the toolbar.
    // MOBILE_SMALL_LANDSCAPE: { sides: 4.5, top: 0.65, bottom: 0.65 },
    MOBILE_SMALL_LANDSCAPE: { sides: 4.75, top: 0.68, bottom: 0.68 }, // 4.5 too crowded (2026-10-16)
    // MOBILE_PORTRAIT: { sides: 0.35, top: 2.5, bottom: 2.5 },
    MOBILE_PORTRAIT: { sides: 0.485, top: 2.5, bottom: 2.5 }, // as MOBILE_SMALL_PORTRAIT (2026-10-30)
    // MOBILE_LANDSCAPE: { sides: 5.25, top: 0.7, bottom: 0.7 },
    // MOBILE_LANDSCAPE: { sides: 4.5, top: 0.65, bottom: 0.65 }, // as MOBILE_SMALL_LANDSCAPE (2026-10-16)
    MOBILE_LANDSCAPE: { sides: 4.75, top: 0.68, bottom: 0.68 }, // as MOBILE_SMALL_LANDSCAPE
    // TABLET_PORTRAIT: { sides: 0.75, top: 3.5, bottom: 3.5 },
    // An iPad upright (2026-10-29): the MOBILE device's portrait UI with this
    // padding (DimensionsContext) — MOBILE_PORTRAIT's, more top / bottom: the
    // reels ~10% smaller, the plates clear of the menu / sound row.
    // TABLET_PORTRAIT: { sides: 0.35, top: 3.0, bottom: 3.0 },
    // TABLET_PORTRAIT: { sides: 0.35, top: 2.85, bottom: 2.85 }, // a bit bigger (2026-10-29)
    // Bigger again — the reel may run past the 9:16 column, as theirs does.
    // TABLET_PORTRAIT: { sides: 0.35, top: 2.6, bottom: 2.6 },
    TABLET_PORTRAIT: { sides: 0.35, top: 2.7, bottom: 2.7 }, // a tiny bit smaller: fits between the buttons
    // TABLET_LANDSCAPE: { sides: 5.25, top: 0.8, bottom: 0.8 },
    // an iPad sideways (2026-10-29): MOBILE_LANDSCAPE's (4.75 / 0.68), a bit smaller — the reel
    // ran into the game name at the top left.
    // TABLET_LANDSCAPE: { sides: 5.1, top: 0.75, bottom: 0.75 },
    TABLET_LANDSCAPE: { sides: 5.25, top: 0.78, bottom: 0.78 }, // a notch smaller
    // SMALL_DESKTOP: { sides: 5.25, top: 0.8, bottom: 0.8 },
    // SMALL_DESKTOP: { sides: 5.25, top: 1.0, bottom: 1.0 }, // more air (2026-10-15)
    SMALL_DESKTOP: { sides: 5.25, top: 1.1, bottom: 1.1 }, // a bit more (2026-10-15)
    // DESKTOP: { sides: 5.25, top: 0.8, bottom: 0.8 },
    // DESKTOP: { sides: 5.25, top: 1.0, bottom: 1.0 }, // more air (2026-10-15)
    // DESKTOP: { sides: 5.25, top: 1.1, bottom: 1.1 }, // a bit more (2026-10-15)
    // Back to 0.8 (2026-10-16): at 1.1 a laptop window drew the scene too
    // small, the background short of the sides.
    // DESKTOP: { sides: 5.25, top: 0.8, bottom: 0.8 },
    // As big as it fits (2026-10-16): ~12 px over the gold bar and over the
    // bottom toolbar on a laptop window (1914x929 → scale ~0.83).
    // DESKTOP: { sides: 5.25, top: 0.62, bottom: 0.62 },
    // A tiny bit smaller (2026-10-16): the free-spins frame's crest was cut at
    // the top. Same table for both rooms.
    DESKTOP: { sides: 5.25, top: 0.7, bottom: 0.7 },
    WIDE_DESKTOP: { sides: 5.25, top: 0.7, bottom: 0.7 },
    // ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 0.7, bottom: 0.7 },
    // Smaller (2026-10-22: "reel is huge") — SMALL_DESKTOP's 1.1.
    // ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 1.1, bottom: 1.1 },
    // Desktop's proportions (2026-10-22): a desktop window's reel is limited
    // by its WIDTH (the side room) — a symbol ~13% of the screen's height, and
    // every popup is drawn from that size. Ultra-wide is limited by the HEIGHT
    // only, so 1.1 still gave ~16% and the popups ran off the screen (H&W
    // intro). 1.75 brings the symbol back to desktop's share of the height.
    // ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 1.75, bottom: 1.75 },
    // ~10% bigger (2026-10-22: "we shrunk everything too much").
    ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 1.4, bottom: 1.4 },
  }),

  ORIENTATION: {
    PORTRAIT: "PORTRAIT",
    LANDSCAPE: "LANDSCAPE",
  },

  DEVICE: {
    MOBILE_SMALL: "MOBILE_SMALL",
    MOBILE: "MOBILE",
    TABLET: "TABLET",
    DESKTOP: "DESKTOP",
  },

  /**
   * Centralized font style presets.
   * Only visual appearance (fill, stroke, gradient, shadow) — not size/weight/layout.
   * Change these once per game to restyle all text across every component.
   */
  FONT_STYLES: {
    /**
     * PRIMARY — Two-layer stacked text (shadow bottom + white top).
     * Used by: WinAnimation, IntroPopup, BuyBonusController
     */
    PRIMARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      shadow: {
        fill: ["#7b4cbf", "#5a2bb0", "#7b4cbf"],
        fillGradientType: 0,
        stroke: "#3a1878",
        strokeThickness: 4,
        lineJoin: "round",
      },
      top: {
        fill: "#ffffff",
        stroke: "#3a1878",
        strokeThickness: 3,
        lineJoin: "round",
      },
      shadowOffsetY: 3,
    },

    /**
     * SECONDARY — Single-layer text with drop shadow.
     * Used by: ReelFrontController (LINES text)
     */
    SECONDARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      fill: "#a1cc02",
      stroke: "#4a5e01",
      strokeThickness: 2,
      dropShadow: true,
      dropShadowColor: "#1a2600",
      dropShadowDistance: 2,
      dropShadowBlur: 0,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
    },

    /**
     * SLOT_TEXT — Shared layout preset for all spine-slot-attached text.
     * Defines fontWeight, fontSize, padding, and resolution so every
     * slot text (IntroPopup cards, max win, tap-to-start, WinAnimation button)
     * looks identical. Spread PRIMARY first, then SLOT_TEXT to override layout props.
     */
    SLOT_TEXT: {
      fontWeight: "400",
      fontSize: 38,
      align: "center",
      letterSpacing: 0,
      padding: 10,
      resolution: 1,
    },
  },
};

/**
 * Helper: returns true if the device is any mobile phone type (MOBILE or MOBILE_SMALL).
 * Use this in direct conditionals where MOBILE_SMALL should behave the same as MOBILE.
 * For per-device configs, use getResponsiveConfig which auto-falls back MOBILE_SMALL → MOBILE.
 */
export const isMobileDevice = (device) =>
  device === PixiSettings.DEVICE.MOBILE ||
  device === PixiSettings.DEVICE.MOBILE_SMALL;

// ---------------------------------------------------------------------------
// Global font configuration (theme-aware)
// ---------------------------------------------------------------------------
export const FONT_CONFIG = {
  PRIMARY: '"Noto Sans", sans-serif',
  SECONDARY: "Arial",
};

// ---------------------------------------------------------------------------
// Global text style configuration
// ---------------------------------------------------------------------------
export const TEXT_STYLE_CONFIG = {
  OUTLINE: {
    dropShadow: true,
    dropShadowColor: "rgba(0, 0, 0, 0.08)",
    dropShadowBlur: 3,
    dropShadowAngle: Math.PI / 4,
    dropShadowDistance: 0.8,
  },
};

// The OUTLINE shadow for a text of `fontSize` px (2026-10-16). Its blur and
// offset were pixels, made for the desktop toolbar's ~28 px text; on a small
// phone (10-14 px text) a 3 px blur smudged the letters. Now they are a share
// of the text's size, so every text wearing it looks the same at any size:
// OUTLINE exactly at 28 px.
const OUTLINE_REFERENCE_FONT_SIZE = 28;
export const outlineShadow = (fontSize) => {
  const share = (fontSize || OUTLINE_REFERENCE_FONT_SIZE) / OUTLINE_REFERENCE_FONT_SIZE;
  return {
    ...TEXT_STYLE_CONFIG.OUTLINE,
    dropShadowBlur: TEXT_STYLE_CONFIG.OUTLINE.dropShadowBlur * share,
    dropShadowDistance: TEXT_STYLE_CONFIG.OUTLINE.dropShadowDistance * share,
  };
};

// ---------------------------------------------------------------------------
// Shared UI button sizing (used by toolbar hooks, displays, logos, scrollbar)
// Individual buttons inline their own copy for independent tuning.
// ---------------------------------------------------------------------------
export const USER_INTERFACE_BUTTON_CONFIG = {
  BASE_WIDTH: 58,
  BASE_HEIGHT: 58,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.05,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.225,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// ---------------------------------------------------------------------------
// Primary UI button sizing (used by primary theme buttons and menu toolbar)
// ---------------------------------------------------------------------------
export const PRIMARY_USER_INTERFACE_BUTTON_CONFIG = {
  BASE_WIDTH: 36,
  BASE_HEIGHT: 36,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.85,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.65,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.1,
    },
  },
};

// ---------------------------------------------------------------------------
// Shared overlay configuration — every popup and full-screen backdrop.
// Used by: WinAnimationSecondary, GambleDialog, PotCollectionGrandJackpot,
//          PotCollectionMaxWin, PotCollectionOutro
//
// One source, so nothing can darken the game to a different depth than
// anything else. Was 0x090020 at 0.6 for Zeus, then black at 0.85 — which
// read as too heavy against the Mad Labs art in the demo.
// ---------------------------------------------------------------------------
export const POPUP_OVERLAY_CONFIG = {
  COLOR: 0x000000,
  ALPHA: 0.75,
  // The bonus intro cards (FS intro, H&W intro, FS retrigger) — PO, 2026-10-09.
  FEATURE_INTRO_ALPHA: 0.95,
  FADE_DURATION: 350,
};
